import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import useCountries from '@afosto/utils-react/useCountries';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Checkbox from '@afosto/app-components-react/Checkbox';
import CountrySelect from '@afosto/app-components-react/CountrySelect';
import Form from '@afosto/app-components-react/Form';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import ServicePointSearch from '@afosto/app-components-react/ServicePointSearch';
import ShippingMethodSearch from '@afosto/app-components-react/ShippingMethodSearch';
import TextField from '@afosto/app-components-react/TextField';
import validationSchema from './validations';
import translations from './translations';
import DatePicker from '../DatePicker';

const ShippingMethodForm = ({
  clientId,
  countryCode: providedCountryCode,
  onCancel,
  onSubmit,
  postalCode,
  shipment,
  showSplitOption,
  showDueAt,
}) => {
  const intl = useIntl();
  const rawCountries = useCountries();
  const countries = rawCountries.map(rawCountry => ({
    label: rawCountry.name,
    ...rawCountry,
  }));
  const formattedCountry = countries.find(({ value }) => value === providedCountryCode);
  const formattedShippingMethod = shipment?.method
    ? { ...shipment?.method, label: shipment?.method?.name, value: shipment?.method?.id }
    : null;
  const formattedServicePoint = shipment?.servicePoint
    ? {
        ...shipment?.servicePoint,
        label: shipment?.servicePoint?.name,
        value: shipment?.servicePoint?.id,
      }
    : null;

  const form = useForm({
    validationSchema,
    defaultValues: {
      method: formattedShippingMethod,
      country: formattedCountry ?? null,
      postalCode: postalCode ?? '',
      servicePoint: formattedServicePoint,
      isSplitAllowed: shipment?.isSplitAllowed ?? false,
      deliveryAt: shipment?.deliveryAt ?? null,
    },
    onCancel,
    onSubmit,
  });
  const { watch, setValue } = form;
  const countryValue = watch('country');
  const methodValue = watch('method');
  const postalCodeValue = watch('postalCode');

  const countryCode = countryValue ? countryValue?.value : providedCountryCode;

  useEffect(() => {
    setValue('servicePoint', null);
  }, [postalCodeValue, setValue]);

  const handleMethodChange = (event, newValue) => {
    setValue('method', newValue);

    if (!newValue?.is_carrier_location) {
      setValue('servicePoint', null);
    }
  };

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form}>
        {!providedCountryCode && (
          <CountrySelect
            name="country"
            label={intl.formatMessage(translations.fields.country.label)}
            options={countries}
            disableClearable
          />
        )}
        <ShippingMethodSearch
          clientId={clientId}
          name="method"
          label={intl.formatMessage(translations.fields.method.label)}
          countryCode={countryCode}
          onChange={handleMethodChange}
        />
        {methodValue?.is_carrier_location && (
          <>
            <TextField
              name="postalCode"
              label={intl.formatMessage(translations.fields.postalCode.label)}
            />

            <ServicePointSearch
              name="servicePoint"
              label={intl.formatMessage(translations.fields.servicePoint.label)}
              carrier={methodValue?.carrier_code}
              postalCode={postalCodeValue}
              countryCode={countryCode}
            />
          </>
        )}
        {showSplitOption && (
          <Checkbox
            name="isSplitAllowed"
            label={intl.formatMessage(translations.fields.isSplitAllowed.label)}
          />
        )}
        {showDueAt && (
          <DatePicker
            name="deliveryAt"
            label={intl.formatMessage(translations.fields.deliveryAt.label)}
            disableFuture={false}
            disablePast
          />
        )}
      </Form>
    </LoadingWrapper>
  );
};

ShippingMethodForm.propTypes = {
  clientId: PropTypes.string,
  countryCode: PropTypes.string,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  postalCode: PropTypes.string,
  shipment: PropTypes.object,
  showSplitOption: PropTypes.bool,
  showDueAt: PropTypes.bool,
};

ShippingMethodForm.defaultProps = {
  clientId: undefined,
  countryCode: 'NL',
  onCancel: undefined,
  onSubmit: undefined,
  postalCode: undefined,
  shipment: undefined,
  showSplitOption: true,
  showDueAt: false,
};

export default ShippingMethodForm;
